import * as React from 'react';
import { useState } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    SaveButton,
    Toolbar,
    TextField,
    Labeled,
    DateField,
    Button,
    TopToolbar,
    useDataProvider,
    useNotify,
    useRecordContext,
    useRefresh,
    useTranslate,
} from 'react-admin';
import { Link } from 'react-router-dom';
import { required } from 'react-admin';
import { useWatch, useFormContext } from 'react-hook-form';
import {
    Grid,
    Box,
    Typography,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button as MuiButton,
    TextField as MuiTextField,
    FormControlLabel,
    Checkbox,
    MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import InfoSchemaForm, {
    getOAuthBasicAuthFields,
} from '../../components/InfoSchemaForm';
import UserSearchWidget from '../../components/UserSearchWidget';

const USER_GUID_WIDGETS = { UserSearchWidget };
const USER_GUID_UI_SCHEMA = {
    user_guid: { 'ui:widget': 'UserSearchWidget' },
};

const APPLICATION_CHOICES = [
    {
        id: '7f7421b3-0aa9-40ce-82d0-f76dc5660a37',
        name: 'Opettaja (opepalvelu)',
    },
    { id: '5b846602-4428-4c2d-b12a-d2b56dd722b7', name: 'Oppilas' },
    { id: 'e0e0e231-be98-43ae-bfb6-3e136bf17f43', name: 'Opiskelija' },
];

const EditActions = () => {
    const translate = useTranslate();
    return (
        <TopToolbar>
            <Button
                label={translate('hallinta.oauth_clients.actions.back')}
                component={Link}
                to="/hallinta?tab=5"
            >
                <ArrowBackIcon />
            </Button>
        </TopToolbar>
    );
};

const EditToolbar = () => (
    <Toolbar>
        <SaveButton />
    </Toolbar>
);

// ==================== Applications Tab ====================

interface AppLinkDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    initialData?: any;
    isEdit: boolean;
}

const AppLinkDialog = ({
    open,
    onClose,
    onSave,
    initialData,
    isEdit,
}: AppLinkDialogProps) => {
    const translate = useTranslate();
    const [applicationGuid, setApplicationGuid] = useState(
        initialData?.application_guid || ''
    );
    const [redirectUris, setRedirectUris] = useState(
        initialData?.supported?.redirect_uri?.join('\n') || ''
    );
    const [loginUri, setLoginUri] = useState(
        initialData?.supported?.login_uri || ''
    );
    const [authorizeUris, setAuthorizeUris] = useState(
        initialData?.supported?.authorize_uri?.join('\n') || ''
    );
    const [scope, setScope] = useState(
        initialData?.supported?.scope?.join(', ') || ''
    );
    const [responseType, setResponseType] = useState(
        initialData?.supported?.response_type?.join(', ') || ''
    );
    const [accessTokenName, setAccessTokenName] = useState(
        initialData?.supported?.access_token_name || ''
    );
    const [useRefreshToken, setUseRefreshToken] = useState(
        initialData?.supported?.use_refresh_token ?? false
    );

    React.useEffect(() => {
        setApplicationGuid(initialData?.application_guid || '');
        setRedirectUris(initialData?.supported?.redirect_uri?.join('\n') || '');
        setLoginUri(initialData?.supported?.login_uri || '');
        setAuthorizeUris(
            initialData?.supported?.authorize_uri?.join('\n') || ''
        );
        setScope(initialData?.supported?.scope?.join(', ') || '');
        setResponseType(
            initialData?.supported?.response_type?.join(', ') || ''
        );
        setAccessTokenName(initialData?.supported?.access_token_name || '');
        setUseRefreshToken(initialData?.supported?.use_refresh_token ?? false);
    }, [initialData, open]);

    const handleSave = () => {
        const supported: any = {};
        if (redirectUris.trim())
            supported.redirect_uri = redirectUris
                .split('\n')
                .map(s => s.trim())
                .filter(Boolean);
        if (loginUri.trim()) supported.login_uri = loginUri.trim();
        if (authorizeUris.trim())
            supported.authorize_uri = authorizeUris
                .split('\n')
                .map(s => s.trim())
                .filter(Boolean);
        if (scope.trim())
            supported.scope = scope
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);
        if (responseType.trim())
            supported.response_type = responseType
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);
        if (accessTokenName.trim())
            supported.access_token_name = accessTokenName.trim();
        supported.use_refresh_token = useRefreshToken;

        onSave({
            application_guid: applicationGuid,
            supported:
                Object.keys(supported).length > 0 ? supported : undefined,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isEdit
                    ? translate(
                          'hallinta.oauth_clients.applications.dialog.edit_title'
                      )
                    : translate(
                          'hallinta.oauth_clients.applications.dialog.add_title'
                      )}
            </DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 1,
                    }}
                >
                    {isEdit ? (
                        <MuiTextField
                            label={translate(
                                'hallinta.oauth_clients.applications.fields.application'
                            )}
                            value={
                                initialData?.application_name || applicationGuid
                            }
                            fullWidth
                            disabled
                        />
                    ) : (
                        <MuiTextField
                            select
                            label={translate(
                                'hallinta.oauth_clients.applications.fields.application'
                            )}
                            value={applicationGuid}
                            onChange={e => setApplicationGuid(e.target.value)}
                            required
                            fullWidth
                        >
                            {APPLICATION_CHOICES.map(app => (
                                <MenuItem key={app.id} value={app.id}>
                                    {app.name}
                                </MenuItem>
                            ))}
                        </MuiTextField>
                    )}
                    <MuiTextField
                        label={translate(
                            'hallinta.oauth_clients.applications.fields.redirect_uris'
                        )}
                        value={redirectUris}
                        onChange={e => setRedirectUris(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        helperText={translate(
                            'hallinta.oauth_clients.applications.dialog.redirect_uris_helper'
                        )}
                    />
                    <MuiTextField
                        label={translate(
                            'hallinta.oauth_clients.applications.fields.login_uri'
                        )}
                        value={loginUri}
                        onChange={e => setLoginUri(e.target.value)}
                        fullWidth
                    />
                    <MuiTextField
                        label={translate(
                            'hallinta.oauth_clients.applications.fields.authorize_uris'
                        )}
                        value={authorizeUris}
                        onChange={e => setAuthorizeUris(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        helperText={translate(
                            'hallinta.oauth_clients.applications.dialog.uris_helper'
                        )}
                    />
                    <MuiTextField
                        label={translate(
                            'hallinta.oauth_clients.applications.fields.scope'
                        )}
                        value={scope}
                        onChange={e => setScope(e.target.value)}
                        fullWidth
                        helperText={translate(
                            'hallinta.oauth_clients.applications.dialog.comma_separated_helper'
                        )}
                    />
                    <MuiTextField
                        label={translate(
                            'hallinta.oauth_clients.applications.fields.response_type'
                        )}
                        value={responseType}
                        onChange={e => setResponseType(e.target.value)}
                        fullWidth
                        helperText={translate(
                            'hallinta.oauth_clients.applications.dialog.comma_separated_helper'
                        )}
                    />
                    <MuiTextField
                        label={translate(
                            'hallinta.oauth_clients.applications.fields.access_token_name'
                        )}
                        value={accessTokenName}
                        onChange={e => setAccessTokenName(e.target.value)}
                        fullWidth
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={useRefreshToken}
                                onChange={e =>
                                    setUseRefreshToken(e.target.checked)
                                }
                            />
                        }
                        label={translate(
                            'hallinta.oauth_clients.applications.fields.use_refresh_token'
                        )}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <MuiButton onClick={onClose}>
                    {translate(
                        'hallinta.oauth_clients.applications.dialog.cancel'
                    )}
                </MuiButton>
                <MuiButton
                    onClick={handleSave}
                    variant="contained"
                    disabled={!applicationGuid}
                >
                    {isEdit
                        ? translate(
                              'hallinta.oauth_clients.applications.dialog.save'
                          )
                        : translate(
                              'hallinta.oauth_clients.applications.dialog.add'
                          )}
                </MuiButton>
            </DialogActions>
        </Dialog>
    );
};

const ApplicationsTab = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editLink, setEditLink] = useState<any>(null);

    if (!record) return null;

    const apps = record.applications || [];

    const handleAdd = () => {
        setEditLink(null);
        setDialogOpen(true);
    };

    const handleEdit = (app: any) => {
        setEditLink(app);
        setDialogOpen(true);
    };

    const handleDelete = async (ocaGuid: string) => {
        if (
            !window.confirm(
                translate('hallinta.oauth_clients.applications.confirm_delete')
            )
        )
            return;
        try {
            await (dataProvider as any).deleteOAuthClientApp(
                record.guid,
                ocaGuid
            );
            notify(
                translate('hallinta.oauth_clients.applications.notify.removed'),
                { type: 'success' }
            );
            refresh();
        } catch (err: any) {
            notify(`Error: ${err.message}`, { type: 'error' });
        }
    };

    const handleSave = async (data: any) => {
        try {
            if (editLink) {
                await (dataProvider as any).updateOAuthClientApp(
                    record.guid,
                    editLink.guid,
                    data
                );
                notify(
                    translate(
                        'hallinta.oauth_clients.applications.notify.updated'
                    ),
                    { type: 'success' }
                );
            } else {
                await (dataProvider as any).createOAuthClientApp(
                    record.guid,
                    data
                );
                notify(
                    translate(
                        'hallinta.oauth_clients.applications.notify.added'
                    ),
                    { type: 'success' }
                );
            }
            setDialogOpen(false);
            refresh();
        } catch (err: any) {
            notify(`Error: ${err.message}`, { type: 'error' });
        }
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AppsOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'black' }}
                    />
                    <Typography variant="subtitle1">
                        {translate(
                            'hallinta.oauth_clients.applications.title_openid'
                        )}
                    </Typography>
                </Stack>
                <MuiButton
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    size="small"
                >
                    {translate(
                        'hallinta.oauth_clients.actions.add_application'
                    )}
                </MuiButton>
            </Box>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                {translate(
                                    'hallinta.oauth_clients.applications.fields.application'
                                )}
                            </TableCell>
                            <TableCell>
                                {translate(
                                    'hallinta.oauth_clients.applications.fields.scope'
                                )}
                            </TableCell>
                            <TableCell>
                                {translate(
                                    'hallinta.oauth_clients.applications.fields.redirect_uris'
                                )}
                            </TableCell>
                            <TableCell>
                                {translate(
                                    'hallinta.oauth_clients.applications.fields.login_uri'
                                )}
                            </TableCell>
                            <TableCell width={100}>
                                {translate(
                                    'hallinta.oauth_clients.applications.fields.status'
                                )}
                            </TableCell>
                            <TableCell width={140}>
                                {translate(
                                    'hallinta.oauth_clients.applications.fields.actions'
                                )}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {apps.length > 0 ? (
                            apps.map((app: any) => (
                                <TableRow key={app.guid}>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {app.application_name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {app.application_guid}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{ maxWidth: 120 }}
                                        >
                                            {app.supported?.scope?.join(', ') ||
                                                '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {app.supported?.redirect_uri?.length ? (
                                            <Tooltip
                                                title={app.supported.redirect_uri.join(
                                                    '\n'
                                                )}
                                            >
                                                <Chip
                                                    label={`${app.supported.redirect_uri.length} URI${app.supported.redirect_uri.length !== 1 ? 's' : ''}`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Tooltip>
                                        ) : (
                                            '—'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{ maxWidth: 400 }}
                                        >
                                            {app.supported?.login_uri || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={translate(
                                                `hallinta.oauth_clients.status.${app.status === 0 ? 'active' : 'disabled'}`
                                            )}
                                            color={
                                                app.status === 0
                                                    ? 'success'
                                                    : 'default'
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEdit(app)}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        {app.api_config_guid && (
                                            <Tooltip title="API Config">
                                                <IconButton
                                                    size="small"
                                                    component={Link}
                                                    to={`/admin/api-configs/${app.api_config_guid}`}
                                                >
                                                    <SettingsOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                handleDelete(app.guid)
                                            }
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {translate(
                                            'hallinta.oauth_clients.applications.no_links'
                                        )}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <AppLinkDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSave}
                initialData={editLink}
                isEdit={!!editLink}
            />
        </Box>
    );
};

// ==================== Main Edit Component ====================

const TYPE_CHOICES = [
    { id: 'openid', name: 'OpenID' },
    { id: 'apikey', name: 'API Key' },
];

const InfoSection = ({
    include,
    exclude,
}: {
    include?: string[];
    exclude?: string[];
}) => {
    const type = useWatch({ name: 'type' });
    const { setValue } = useFormContext();
    const prevTypeRef = React.useRef(type);

    React.useEffect(() => {
        if (prevTypeRef.current && prevTypeRef.current !== type) {
            // Type changed — reset info to defaults for the new type
            setValue('info', { type }, { shouldDirty: true });
        }
        prevTypeRef.current = type;
    }, [type, setValue]);

    if (!type) return null;
    // The user_guid field belongs to the Basic Auth section (include); render it
    // with the user search widget there so admins don't need to paste a GUID.
    const renderUserSearch = !!include?.includes('user_guid');
    return (
        <InfoSchemaForm
            key={type}
            resource="oauth-client"
            type={type}
            source="info"
            include={include}
            exclude={exclude}
            widgets={renderUserSearch ? USER_GUID_WIDGETS : undefined}
            uiSchema={renderUserSearch ? USER_GUID_UI_SCHEMA : undefined}
        />
    );
};

const ApplicationsSection = () => {
    const type = useWatch({ name: 'type' });
    if (type && type !== 'openid') return null;
    return <ApplicationsTab />;
};

const OAuthClientEditForm = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const type = useWatch({ name: 'type' });
    const isOpenid = !type || type === 'openid';
    const basicAuthFields = getOAuthBasicAuthFields(type || 'openid');

    const statusChoices = [
        { id: 0, name: translate('hallinta.oauth_clients.status.active') },
        { id: 1, name: translate('hallinta.oauth_clients.status.disabled') },
    ];

    return (
        <Grid container spacing={3} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, md: isOpenid ? 4 : 12 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 2 }}
                >
                    <InfoOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'black' }}
                    />
                    <Typography variant="subtitle1">
                        {translate('hallinta.oauth_clients.tabs.basic_info')}
                    </Typography>
                </Stack>
                <Stack spacing={1}>
                    <Labeled
                        label={translate('hallinta.oauth_clients.fields.guid')}
                    >
                        <TextField source="guid" />
                    </Labeled>
                    <TextInput
                        source="name"
                        label={translate('hallinta.oauth_clients.fields.name')}
                        validate={[required()]}
                        fullWidth
                    />
                    <SelectInput
                        source="type"
                        label={translate('hallinta.oauth_clients.fields.type')}
                        choices={TYPE_CHOICES}
                        fullWidth
                        defaultValue="openid"
                        disabled
                    />
                    <SelectInput
                        source="status"
                        label={translate(
                            'hallinta.oauth_clients.fields.status'
                        )}
                        choices={statusChoices}
                        fullWidth
                    />
                </Stack>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 3, mb: 1 }}
                >
                    <SettingsOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'black' }}
                    />
                    <Typography variant="subtitle1">
                        {translate(
                            isOpenid
                                ? 'hallinta.oauth_clients.sections.info_config_openid'
                                : 'hallinta.oauth_clients.sections.info_config'
                        )}
                    </Typography>
                </Stack>
                <InfoSection exclude={basicAuthFields} />
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 3, mb: 1 }}
                >
                    <VpnKeyOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'black' }}
                    />
                    <Typography variant="subtitle1">
                        {translate(
                            'hallinta.oauth_clients.sections.basic_auth'
                        )}
                    </Typography>
                </Stack>
                <InfoSection include={basicAuthFields} />
                <Typography
                    variant="subtitle2"
                    sx={{ mt: 2 }}
                    color="text.secondary"
                >
                    {translate('hallinta.oauth_clients.audit')}
                </Typography>
                <Stack direction="row" spacing={4}>
                    <Labeled
                        label={translate(
                            'hallinta.oauth_clients.fields.created'
                        )}
                    >
                        <DateField source="created" showTime locales="fi-FI" />
                    </Labeled>
                    <Labeled
                        label={translate(
                            'hallinta.oauth_clients.fields.modified'
                        )}
                    >
                        <DateField source="modified" showTime locales="fi-FI" />
                    </Labeled>
                </Stack>
            </Grid>
            {isOpenid && (
                <Grid size={{ xs: 12, md: 8 }}>
                    <ApplicationsSection />
                </Grid>
            )}
        </Grid>
    );
};

const transformSubmit = (data: any) => {
    // Strip flat fields that are now in info — backend reads from info
    const {
        client_id,
        client_secret,
        api_key,
        env,
        theme,
        back_channel_logout_uri,
        back_channel_logout_apps,
        applications,
        jwt_keys,
        ...rest
    } = data;
    return rest;
};

const FormWrapper = () => {
    const record = useRecordContext();
    if (!record) return null;

    // Flatten info.private → info for the InfoSchemaForm
    const transformed = React.useMemo(() => {
        if (record?.info?.private) {
            return { ...record, info: record.info.private };
        }
        return record;
    }, [record]);

    return (
        <SimpleForm toolbar={<EditToolbar />} record={transformed}>
            <OAuthClientEditForm />
        </SimpleForm>
    );
};

const OAuthClientEdit = () => (
    <Edit
        redirect={false}
        mutationMode="optimistic"
        actions={<EditActions />}
        transform={transformSubmit}
        queryOptions={{ refetchOnMount: 'always' }}
    >
        <FormWrapper />
    </Edit>
);

export default OAuthClientEdit;
